if (!customElements.get('certificate-form')) {
  customElements.define(
    'certificate-form',
    class CertificateForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.template = this.querySelector('[data-certificate-template]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.errorMessage = this.querySelector('[data-certificate-error]');

        if (!this.form) return;

        this.form.addEventListener('submit', this.#onSubmit);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.#onSubmit);
      }

      #onSubmit = async (event) => {
        event.preventDefault();

        if (!this.form.reportValidity()) return;

        this.#setLoading(true);
        this.#toggleError(false);

        try {
          const values = this.#getValues();
          const article = this.#buildCertificateArticle(values);
          const jpegBytes = await this.#renderJpeg(article);
          const pdfBytes = createPdfFromJpeg(jpegBytes);
          const filename = this.#getFilename(values.invoiceNumber);

          downloadBytes(pdfBytes, filename, 'application/pdf');
        } catch (error) {
          console.error(error);
          this.#toggleError(true);
        } finally {
          this.#setLoading(false);
        }
      };

      #getValues() {
        const formData = new FormData(this.form);
        const invoiceDate = formatDate(formData.get('invoiceDate'));

        return {
          deliveredDate: invoiceDate,
          invoiceNumber: String(formData.get('invoiceNumber') || '').trim(),
          invoiceDate,
          kilograms: String(formData.get('kilograms') || '').trim(),
          lotNumber: this.dataset.lotNumber || '',
        };
      }

      #buildCertificateArticle(values) {
        const fragment = this.template.content.cloneNode(true);
        const article = fragment.querySelector('.certificate-doc');

        for (const field of article.querySelectorAll('[data-field]')) {
          field.textContent = values[field.dataset.field] || '';
        }

        return article;
      }

      async #renderJpeg(article) {
        const canvas = document.createElement('canvas');
        const scale = 2;

        canvas.width = Math.round(PAGE_WIDTH * scale);
        canvas.height = Math.round(PAGE_HEIGHT * scale);

        const ctx = canvas.getContext('2d');

        ctx.scale(scale, scale);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, PAGE_WIDTH, PAGE_HEIGHT);
        ctx.fillStyle = '#111111';
        ctx.textBaseline = 'alphabetic';

        this.#drawArticle(ctx, article);

        const blob = await canvasToJpeg(canvas);
        const buffer = await blob.arrayBuffer();

        return new Uint8Array(buffer);
      }

      #drawArticle(ctx, article) {
        const marginX = 56;
        const maxWidth = PAGE_WIDTH - marginX * 2;
        let y = 68;
        const lineHeight = 15;

        for (const child of article.children) {
          const text = child.textContent.replace(/\s+/g, ' ').trim();

          if (!text) continue;

          if (child.classList.contains('certificate-doc__line--bold')) {
            ctx.font = 'bold 12px "Times New Roman", Times, serif';
            y = drawParagraph(ctx, text, marginX, y, maxWidth, 16);
            continue;
          }

          if (child.classList.contains('certificate-doc__title')) {
            y += 14;
            ctx.font = 'bold 13px "Times New Roman", Times, serif';
            ctx.textAlign = 'center';
            y = drawParagraph(ctx, text, PAGE_WIDTH / 2, y, maxWidth, 18);
            ctx.textAlign = 'left';
            y += 10;
            continue;
          }

          if (child.tagName === 'OL') {
            ctx.font = '11px "Times New Roman", Times, serif';
            let index = 1;

            for (const item of child.children) {
              const itemText = item.textContent.replace(/\s+/g, ' ').trim();

              y = drawListItem(ctx, `${index}. `, itemText, marginX, y, maxWidth, lineHeight);
              y += 3;
              index += 1;
            }

            y += 6;
            continue;
          }

          if (child.tagName === 'UL') {
            ctx.font = '11px "Times New Roman", Times, serif';

            for (const item of child.children) {
              const itemText = item.textContent.replace(/\s+/g, ' ').trim();

              y = drawListItem(ctx, '• ', itemText, marginX, y, maxWidth, lineHeight);
              y += 3;
            }

            y += 4;
            continue;
          }

          ctx.font = '11px "Times New Roman", Times, serif';
          y = drawParagraph(ctx, text, marginX, y, maxWidth, lineHeight);
          y += 4;
        }
      }

      #getFilename(invoiceNumber) {
        const safeName = String(invoiceNumber || '')
          .replace(/[^\w.-]+/g, '-')
          .replace(/^-+|-+$/g, '');

        if (!safeName) return 'certificat-dezinfectie.pdf';

        return `certificat-dezinfectie-${safeName}.pdf`;
      }

      #setLoading(isLoading) {
        if (!this.submitButton) return;

        this.submitButton.disabled = isLoading;
        this.submitButton.setAttribute('aria-busy', String(isLoading));
      }

      #toggleError(visible) {
        if (!this.errorMessage) return;

        this.errorMessage.hidden = !visible;
        this.errorMessage.classList.toggle('hidden', !visible);
      }
    }
  );
}

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const encoder = new TextEncoder();

const formatDate = (value) => {
  if (!value) return '';

  const parts = String(value).split('-');

  if (parts.length !== 3) return String(value);

  const [year, month, day] = parts;

  return `${day}.${month}.${year}`;
};

const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let current = '';

  for (const word of words) {
    const next = current ? `${current} ${word}` : word;

    if (ctx.measureText(next).width > maxWidth && current) {
      lines.push(current);
      current = word;
      continue;
    }

    current = next;
  }

  if (current) lines.push(current);

  return lines;
};

const drawParagraph = (ctx, text, x, y, maxWidth, lineHeight) => {
  const lines = wrapText(ctx, text, maxWidth);

  for (const line of lines) {
    ctx.fillText(line, x, y);
    y += lineHeight;
  }

  return y;
};

const drawListItem = (ctx, prefix, text, x, y, maxWidth, lineHeight) => {
  const prefixWidth = ctx.measureText(prefix).width;
  const lines = wrapText(ctx, text, maxWidth - prefixWidth);

  for (const [index, line] of lines.entries()) {
    const lineText = index === 0 ? `${prefix}${line}` : line;
    const lineX = index === 0 ? x : x + prefixWidth;

    ctx.fillText(lineText, lineX, y);
    y += lineHeight;
  }

  return y;
};

const canvasToJpeg = (canvas) => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create JPEG'));
          return;
        }

        resolve(blob);
      },
      'image/jpeg',
      0.92
    );
  });
};

const concatBytes = (chunks) => {
  const length = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const output = new Uint8Array(length);
  let offset = 0;

  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.length;
  }

  return output;
};

const toBytes = (value) => {
  if (typeof value === 'string') return encoder.encode(value);

  return value;
};

const createPdfFromJpeg = (jpegBytes) => {
  const chunks = [];
  const offsets = [0];
  let position = 0;

  const push = (value) => {
    const bytes = toBytes(value);

    chunks.push(bytes);
    position += bytes.length;
  };

  push('%PDF-1.4\n');

  offsets[1] = position;
  push('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

  offsets[2] = position;
  push('2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n');

  offsets[3] = position;
  push(
    `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>\nendobj\n`
  );

  offsets[4] = position;
  push(
    `4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${Math.round(PAGE_WIDTH * 2)} /Height ${Math.round(
      PAGE_HEIGHT * 2
    )} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpegBytes.length} >>\nstream\n`
  );
  push(jpegBytes);
  push('\nendstream\nendobj\n');

  const content = `q\n${PAGE_WIDTH} 0 0 ${PAGE_HEIGHT} 0 0 cm\n/Im0 Do\nQ\n`;

  offsets[5] = position;
  push(`5 0 obj\n<< /Length ${content.length} >>\nstream\n${content}endstream\nendobj\n`);

  const xrefPosition = position;
  let xref = 'xref\n0 6\n0000000000 65535 f \n';

  for (let index = 1; index <= 5; index++) {
    xref += `${String(offsets[index]).padStart(10, '0')} 00000 n \n`;
  }

  push(xref);
  push(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefPosition}\n%%EOF`);

  return concatBytes(chunks);
};

const downloadBytes = (bytes, filename, mimeType) => {
  const blob = new Blob([bytes], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
